/**
 * TextAnchor - Precise text location anchoring system for EPUB annotations
 *
 * Based on XPath + text context for resilience against content changes.
 * Inspired by Hypothesis annotation anchoring and EPUB CFI concepts.
 *
 * See: CUSTOM_HTML_RENDERER_IMPLEMENTATION_WITH_ANNOTATIONS.md
 */

/**
 * TextAnchor represents a precise location in HTML content
 *
 * Primary: XPath-based range with character offsets
 * Fallback: Text context (prefix/exact/suffix) for fuzzy matching
 */
export interface TextAnchor {
  // XPath-based anchoring (primary)
  startXPath: string; // XPath to start node
  startOffset: number; // Character offset within start node
  endXPath: string; // XPath to end node
  endOffset: number; // Character offset within end node

  // Text context for fallback matching
  textContext: {
    prefix: string; // 50 chars before selection
    exact: string; // Exact selected text
    suffix: string; // 50 chars after selection
  };

  // Metadata
  chapterIndex?: number; // For multi-chapter documents
  timestamp: number; // Creation timestamp
}

/**
 * Serialized TextAnchor for storage in backend Location field
 */
export type SerializedTextAnchor = string; // JSON string

/**
 * Context length for fuzzy matching fallback
 */
const CONTEXT_LENGTH = 50;

/**
 * Generate XPath for a DOM node
 * Returns an absolute XPath expression that uniquely identifies the node
 */
export function getXPathForNode(node: Node, root: Node = document.body): string {
  const segments: string[] = [];
  let currentNode: Node | null = node;

  while (currentNode && currentNode !== root && currentNode.parentNode) {
    const parent = currentNode.parentNode;
    const children = Array.from(parent.childNodes);
    const nodeType = currentNode.nodeType;

    if (nodeType === Node.ELEMENT_NODE) {
      const element = currentNode as Element;
      const tagName = element.tagName.toLowerCase();
      const siblings = children.filter(
        (n) => n.nodeType === Node.ELEMENT_NODE && (n as Element).tagName.toLowerCase() === tagName
      );

      if (siblings.length === 1) {
        segments.unshift(tagName);
      } else {
        const index = siblings.indexOf(currentNode) + 1;
        segments.unshift(`${tagName}[${index}]`);
      }
    } else if (nodeType === Node.TEXT_NODE) {
      const textSiblings = children.filter((n) => n.nodeType === Node.TEXT_NODE);
      if (textSiblings.length === 1) {
        segments.unshift('text()');
      } else {
        const index = textSiblings.indexOf(currentNode) + 1;
        segments.unshift(`text()[${index}]`);
      }
    }

    currentNode = parent;
  }

  return '//' + segments.join('/');
}

/**
 * Resolve XPath to a DOM node
 * Returns null if XPath cannot be resolved
 */
export function resolveXPath(xpath: string, root: Document | Element = document): Node | null {
  try {
    const result = document.evaluate(
      xpath,
      root,
      null,
      XPathResult.FIRST_ORDERED_NODE_TYPE,
      null
    );
    return result.singleNodeValue;
  } catch (err) {
    console.error('Failed to resolve XPath:', xpath, err);
    return null;
  }
}

/**
 * Create TextAnchor from a DOM Range
 * This is called when user selects text for highlighting
 */
export function createTextAnchor(
  range: Range,
  root: Node = document.body,
  chapterIndex?: number
): TextAnchor {
  // Generate XPath for start and end containers
  const startXPath = getXPathForNode(range.startContainer, root);
  const endXPath = getXPathForNode(range.endContainer, root);

  // Extract text context for fallback
  const selectedText = range.toString();
  const textContext = extractTextContext(range, root);

  return {
    startXPath,
    startOffset: range.startOffset,
    endXPath,
    endOffset: range.endOffset,
    textContext: {
      prefix: textContext.prefix,
      exact: selectedText,
      suffix: textContext.suffix,
    },
    chapterIndex,
    timestamp: Date.now(),
  };
}

/**
 * Extract text context around a Range for fallback matching
 */
function extractTextContext(
  range: Range,
  root: Node
): { prefix: string; suffix: string } {
  // Get all text content before and after the selection
  const rootText = root.textContent || '';
  const selectedText = range.toString();

  // Find selection position in root text
  const rangeBefore = document.createRange();
  rangeBefore.setStart(root, 0);
  rangeBefore.setEnd(range.startContainer, range.startOffset);
  const textBefore = rangeBefore.toString();

  const rangeAfter = document.createRange();
  rangeAfter.setStart(range.endContainer, range.endOffset);
  rangeAfter.setEnd(root, root.childNodes.length);
  const textAfter = rangeAfter.toString();

  // Extract context
  const prefix = textBefore.slice(-CONTEXT_LENGTH);
  const suffix = textAfter.slice(0, CONTEXT_LENGTH);

  return { prefix, suffix };
}

/**
 * Restore a DOM Range from a TextAnchor
 * Primary: XPath-based restoration
 * Fallback: Text context fuzzy matching
 */
export function restoreRange(
  anchor: TextAnchor,
  root: Document | Element = document
): Range | null {
  // Try primary XPath-based restoration
  const rangeFromXPath = restoreRangeFromXPath(anchor, root);
  if (rangeFromXPath) {
    return rangeFromXPath;
  }

  // Fallback: Text context matching
  console.warn('XPath restoration failed, trying text context fallback');
  return restoreRangeFromTextContext(anchor, root);
}

/**
 * Restore Range using XPath (primary method)
 */
function restoreRangeFromXPath(
  anchor: TextAnchor,
  root: Document | Element
): Range | null {
  try {
    const startNode = resolveXPath(anchor.startXPath, root);
    const endNode = resolveXPath(anchor.endXPath, root);

    if (!startNode || !endNode) {
      return null;
    }

    // Validate offsets
    const startText = startNode.textContent || '';
    const endText = endNode.textContent || '';

    if (
      anchor.startOffset > startText.length ||
      anchor.endOffset > endText.length
    ) {
      console.warn('Invalid offsets for XPath nodes');
      return null;
    }

    const range = document.createRange();
    range.setStart(startNode, anchor.startOffset);
    range.setEnd(endNode, anchor.endOffset);

    // Verify that restored range matches expected text
    const restoredText = range.toString();
    if (restoredText !== anchor.textContext.exact) {
      console.warn('Restored range text does not match expected text');
      // Still return the range, but log warning
    }

    return range;
  } catch (err) {
    console.error('XPath range restoration error:', err);
    return null;
  }
}

/**
 * Restore Range using text context fuzzy matching (fallback)
 */
function restoreRangeFromTextContext(
  anchor: TextAnchor,
  root: Document | Element
): Range | null {
  const { prefix, exact, suffix } = anchor.textContext;
  const fullText = root.textContent || '';

  // Search for the pattern: prefix + exact + suffix
  const searchPattern = prefix + exact + suffix;
  const patternIndex = fullText.indexOf(searchPattern);

  if (patternIndex === -1) {
    // Try searching for just the exact text
    const exactIndex = fullText.indexOf(exact);
    if (exactIndex === -1) {
      console.error('Text context matching failed: exact text not found');
      return null;
    }

    // Found exact text, but without context - still create range
    return createRangeFromTextOffset(root, exactIndex, exactIndex + exact.length);
  }

  // Found full pattern - create range
  const startOffset = patternIndex + prefix.length;
  const endOffset = startOffset + exact.length;

  return createRangeFromTextOffset(root, startOffset, endOffset);
}

/**
 * Create a Range from character offsets in text content
 * This walks the DOM tree to find the actual nodes at the offsets
 */
function createRangeFromTextOffset(
  root: Node,
  startOffset: number,
  endOffset: number
): Range | null {
  let currentOffset = 0;
  let startNode: Node | null = null;
  let startNodeOffset = 0;
  let endNode: Node | null = null;
  let endNodeOffset = 0;

  // Walk the tree to find nodes at the offsets
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);

  let node: Node | null;
  while ((node = walker.nextNode())) {
    const nodeText = node.textContent || '';
    const nodeLength = nodeText.length;

    // Check if start offset is in this node
    if (!startNode && currentOffset + nodeLength >= startOffset) {
      startNode = node;
      startNodeOffset = startOffset - currentOffset;
    }

    // Check if end offset is in this node
    if (currentOffset + nodeLength >= endOffset) {
      endNode = node;
      endNodeOffset = endOffset - currentOffset;
      break;
    }

    currentOffset += nodeLength;
  }

  if (!startNode || !endNode) {
    console.error('Could not find nodes at offsets');
    return null;
  }

  try {
    const range = document.createRange();
    range.setStart(startNode, startNodeOffset);
    range.setEnd(endNode, endNodeOffset);
    return range;
  } catch (err) {
    console.error('Failed to create range from offsets:', err);
    return null;
  }
}

/**
 * Serialize TextAnchor to JSON string for storage in backend
 */
export function serializeTextAnchor(anchor: TextAnchor): SerializedTextAnchor {
  return JSON.stringify(anchor);
}

/**
 * Deserialize TextAnchor from JSON string stored in backend
 */
export function deserializeTextAnchor(
  serialized: SerializedTextAnchor
): TextAnchor | null {
  try {
    return JSON.parse(serialized) as TextAnchor;
  } catch (err) {
    console.error('Failed to deserialize TextAnchor:', err);
    return null;
  }
}

/**
 * Apply highlights to HTML using TextAnchors
 * This replaces the simple string replacement approach
 */
export function applyHighlightsWithAnchors(
  container: HTMLElement,
  highlights: Array<{
    id: string;
    anchor: TextAnchor;
    color: string;
  }>
): void {
  highlights.forEach(({ id, anchor, color }) => {
    const range = restoreRange(anchor, container);
    if (!range) {
      console.warn(`Failed to restore highlight ${id}`);
      return;
    }

    // Wrap range in a highlight span
    const span = document.createElement('span');
    span.className = 'reader-highlight';
    span.setAttribute('data-highlight-id', id);
    span.style.backgroundColor = color;
    span.style.padding = '2px 0';
    span.style.borderRadius = '2px';
    span.style.cursor = 'pointer';
    span.title = 'Click to view note';

    try {
      range.surroundContents(span);
    } catch (err) {
      // surroundContents fails if range spans multiple elements
      // Fall back to extractContents + appendChild
      try {
        const fragment = range.extractContents();
        span.appendChild(fragment);
        range.insertNode(span);
      } catch (innerErr) {
        console.error(`Failed to apply highlight ${id}:`, innerErr);
      }
    }
  });
}

/**
 * Remove highlight from DOM
 */
export function removeHighlight(highlightId: string, container: HTMLElement): void {
  const span = container.querySelector(
    `span.reader-highlight[data-highlight-id="${highlightId}"]`
  );

  if (span && span.parentNode) {
    // Unwrap the span, preserving its contents
    const fragment = document.createDocumentFragment();
    while (span.firstChild) {
      fragment.appendChild(span.firstChild);
    }
    span.parentNode.replaceChild(fragment, span);
  }
}

/**
 * Validate TextAnchor against current DOM
 * Returns true if anchor can be restored
 */
export function validateTextAnchor(
  anchor: TextAnchor,
  root: Document | Element = document
): boolean {
  const range = restoreRange(anchor, root);
  if (!range) {
    return false;
  }

  // Check if restored text matches expected text
  const restoredText = range.toString();
  return restoredText === anchor.textContext.exact;
}

/**
 * Get selection range from current window selection
 */
export function getSelectionRange(): Range | null {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return null;
  }

  return selection.getRangeAt(0);
}

/**
 * Clear current window selection
 */
export function clearSelection(): void {
  const selection = window.getSelection();
  if (selection) {
    selection.removeAllRanges();
  }
}

/**
 * Get highlight color with opacity for better readability
 */
export function getHighlightColorWithOpacity(
  color: string,
  opacity: number = 0.4
): string {
  // Handle hex colors
  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  // Handle rgb/rgba colors
  if (color.startsWith('rgb')) {
    const match = color.match(/\d+/g);
    if (match && match.length >= 3) {
      const [r, g, b] = match;
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
  }

  // Fallback: return as-is
  return color;
}
